import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import './DatePicker.css';

// Pad a number to two digits.
const pad = (n) => String(n).padStart(2, '0');

// Format a Date as a local YYYY-MM-DD string (no timezone shift).
const toYMD = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

// Parse the incoming value into a local Date, or null when empty/invalid.
// In string mode, YYYY-MM-DD is parsed as a local date so it never shifts a day.
const parseValue = (value, asString) => {
  if (!value) return null;
  if (asString && typeof value === 'string') {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  }
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
};

const DatePicker = ({
  selectedDate,
  onDateChange,
  label = 'Date',
  asString = false,
  hideLabel = false,
  placeholder = 'Select date',
  className = '',
}) => {
  const selected = parseValue(selectedDate, asString);
  const [showCalendar, setShowCalendar] = useState(false);
  const [displayMonth, setDisplayMonth] = useState(selected || new Date());
  const [dropdownStyle, setDropdownStyle] = useState(null);
  const calendarRef = useRef(null);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  const isForm = className.split(' ').includes('dp-form');

  // The dropdown is portaled to <body> so it can't be clipped by ancestor
  // stacking contexts (e.g. cards with backdrop-filter). Position it under
  // the button using fixed coordinates derived from the button's rect.
  const positionDropdown = useCallback(() => {
    if (!buttonRef.current) return;
    const r = buttonRef.current.getBoundingClientRect();
    setDropdownStyle(
      isForm
        ? { top: r.bottom + 8, left: r.left }
        : { top: r.bottom + 8, right: window.innerWidth - r.right }
    );
  }, [isForm]);

  // Close calendar when clicking outside (button container or portaled dropdown)
  useEffect(() => {
    if (!showCalendar) return;

    const handleClickOutside = (event) => {
      const inButton = calendarRef.current && calendarRef.current.contains(event.target);
      const inDropdown = dropdownRef.current && dropdownRef.current.contains(event.target);
      if (!inButton && !inDropdown) {
        setShowCalendar(false);
      }
    };

    const handleReposition = () => positionDropdown();

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [showCalendar, positionDropdown]);

  const emitChange = (date) => {
    onDateChange(asString ? toYMD(date) : date);
  };

  const formatDisplayDate = () => {
    if (!selected) return placeholder;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const day = new Date(selected);
    day.setHours(0, 0, 0, 0);

    if (day.getTime() === today.getTime()) {
      return 'Today';
    }

    const options = { month: 'short', day: 'numeric', year: 'numeric' };
    return selected.toLocaleDateString('en-US', options);
  };

  const toggleCalendar = () => {
    if (!showCalendar) {
      setDisplayMonth(selected || new Date());
      positionDropdown();
    }
    setShowCalendar(!showCalendar);
  };

  const handleDateSelect = (date) => {
    emitChange(date);
    setShowCalendar(false);
  };

  const handleTodayClick = () => {
    const today = new Date();
    emitChange(today);
    setDisplayMonth(today);
    setShowCalendar(false);
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay(); // 0 = Sunday

    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(null);
    }

    // Add the days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }

    return days;
  };

  const changeMonth = (direction) => {
    const newMonth = new Date(displayMonth);
    newMonth.setMonth(newMonth.getMonth() + direction);
    setDisplayMonth(newMonth);
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  const isSelected = (date) => {
    if (!date || !selected) return false;
    return date.getDate() === selected.getDate() &&
           date.getMonth() === selected.getMonth() &&
           date.getFullYear() === selected.getFullYear();
  };

  const isFutureDate = (date) => {
    if (!date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    return checkDate > today;
  };

  const days = getDaysInMonth(displayMonth);
  const monthName = displayMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <div className={`date-picker-container${className ? ` ${className}` : ''}`} ref={calendarRef}>
      {!hideLabel && <div className="date-picker-label">{label}</div>}
      <button
        ref={buttonRef}
        type="button"
        className="date-picker-button"
        onClick={toggleCalendar}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
          <line x1="16" y1="2" x2="16" y2="6"/>
          <line x1="8" y1="2" x2="8" y2="6"/>
          <line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
        <span className={selected ? '' : 'date-picker-placeholder'}>{formatDisplayDate()}</span>
      </button>

      {showCalendar && dropdownStyle && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          className="date-picker-dropdown date-picker-dropdown--portal"
          style={dropdownStyle}
        >
          <div className="calendar-header">
            <button
              type="button"
              className="calendar-nav-btn"
              onClick={() => changeMonth(-1)}
              aria-label="Previous month"
            >
              ‹
            </button>
            <div className="calendar-month">{monthName}</div>
            <button
              type="button"
              className="calendar-nav-btn"
              onClick={() => changeMonth(1)}
              aria-label="Next month"
            >
              ›
            </button>
          </div>

          <div className="calendar-weekdays">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="calendar-weekday">{day}</div>
            ))}
          </div>

          <div className="calendar-days">
            {days.map((date, index) => (
              <button
                type="button"
                key={index}
                className={`calendar-day ${!date ? 'empty' : ''} ${isToday(date) ? 'today' : ''} ${isSelected(date) ? 'selected' : ''} ${isFutureDate(date) ? 'future' : ''}`}
                onClick={() => date && !isFutureDate(date) && handleDateSelect(date)}
                disabled={!date || isFutureDate(date)}
              >
                {date ? date.getDate() : ''}
              </button>
            ))}
          </div>

          <div className="calendar-footer">
            <button
              type="button"
              className="calendar-today-btn"
              onClick={handleTodayClick}
            >
              Today
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default DatePicker;
